<template>
  <div
    v-if="!hide"
    class="col"
    data-test-id="item-variation-buttons"
  >
    <div :class="$q.screen.gt.xs ? 'q-mb-md' : 'q-mb-sm'">
      <div class="text-h5 row justify-between">
        {{ item.name }}
        <template v-if="$register">
          <q-btn
            v-if="!editMode"
            label="Edit Options"
            icon="edit"
            outline
            :loading="configuration.loading"
            @click="$emit('edit-options-button-click', () => toggleEditMode())"
          />
        </template>
      </div>
      <div> <b>{{ $t('common.required') }}</b> <span class="q-mx-xs"> &middot;</span> Choose 1</div>
    </div>

    <div :style="editMode ? 'border: 2px solid var(--q-info);' : ''">
      <div
        v-if="editMode"
        class="full-width row items-center q-pa-sm q-mb-md bg-info"
        :class="$isColorDark(currentSite.infoColor) ? 'text-white' : 'text-black'"
      >
        <div class="col">
          <div class="row items-center">
            <q-icon
              class="col-auto q-mr-md"
              size="md"
              name="info"
            />
            <div class="col">
              <div class="text-h5">
                Editing Sellable Item Variations
              </div>
              <div>
                Toggle each item variation to enable (on) or disable (off) sales.
              </div>
            </div>
          </div>
        </div>
        <div class="col-auto">
          <q-btn
            class="q-mr-md"
            label="Cancel"
            icon="cancel"
            outline
            color="negative"
            @click="toggleEditMode()"
          />
          <q-btn
            label="Save & Close"
            icon="save"
            color="primary"
            :loading="savingEdits || configuration.loading"
            :disable="!hasEdits"
            @click="handleSaveEdits"
          />
        </div>
      </div>

      <div
        class="row"
        :class="$q.screen.gt.xs ? 'q-col-gutter-lg' : 'q-col-gutter-sm'"
      >
        <div
          v-for="itemVariation in availableItemVariations"
          :key="itemVariation.id"
          class="items-start col-12 col-sm-6"
          :class="{ 'col-lg-4': round }"
        >
          <q-card
            flat
            bordered
            class="position-relative rounded-borders-8"
            :class="{ 'bg-responsive-grey-2': itemOrderItem.itemVariationSelected(itemVariation) }"
            :style="{
              'border-radius': round ? '24px' : undefined,
              'border-width': itemOrderItem.itemVariationSelected(itemVariation) ? '2px' : undefined,
              'border-color': itemOrderItem.itemVariationSelected(itemVariation) ? currentSite.primaryColor : undefined
            }"
            @click="handleClick(itemVariation)"
          >
            <q-card-section
              horizontal
              class="justify-between items-center q-pa-sm"
              :style="!editMode && hasReducedOpacity(itemVariation, { item, itemOrderItem, orderItem }) ? 'opacity: 0.3;' : ''"
            >
              <q-icon
                :name="itemOrderItem.itemVariationSelected(itemVariation) ? 'radio_button_checked' : 'radio_button_unchecked'"
                :color="itemOrderItem.itemVariationSelected(itemVariation) ? 'primary' :'responsive'"
                class="col-auto q-mr-sm"
                :class="{ 'bg-white': itemOrderItem.itemVariationSelected(itemVariation) }"
                style="border-radius: 15px; width: 30px; height: 30px;"
                size="md"
              />
              <q-img
                v-if="itemVariation.imageUrl"
                :ratio="1"
                class="rounded-borders q-mr-sm"
                :src="itemVariation.imageUrl"
                :style="{
                  width: $q.screen.lt.sm? '20px' : '30px',
                  height: $q.screen.lt.sm? '20px' : '30px',
                  'background-color': itemVariation.color
                }"
              />

              <div
                class="col"
              >
                <div
                  :class="$q.screen.lt.sm ? 'text-h6' : 'text-h5'"
                  style="line-height: 1.5;"
                >
                  {{ itemVariation.name }}
                </div>
                <div
                  v-if="itemVariation.description"
                  class="text-caption"
                  style="line-height: 1;"
                >
                  {{ itemVariation.description }}
                </div>
              </div>
              <q-toggle
                v-if="editMode"
                :model-value="areSalesEnabled(itemVariation)"
                :disable="itemVariation.loading || configuration.loading"
                @update:model-value="toggleSalesDisabled(itemVariation)"
              />
              <slot
                v-else
                name="price"
              >
                <div
                  v-if="itemVariation.price && displayPrice"
                  class="col-auto"
                  style="min-width: 50px;"
                >
                  <i18n-n
                    class="text-subtitle1"
                    tag="span"
                    :value="itemVariation.price"
                    format="currency"
                    scope="global"
                  />
                </div>
              </slot>
            </q-card-section>
            <q-badge
              v-if="isAvailabilityBadgeVisible(itemVariation, { item, itemOrderItem, orderItem })"
              class="absolute-top-right"
              :color="availabilityBadgeColor(itemVariation, { item, itemOrderItem, orderItem })"
              :label="availableStock(itemVariation, { item, itemOrderItem, orderItem })"
            />
          </q-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SALES_ITEM_RESTRICTION_TYPES } from 'src/models/inventory-strategy/InventoryStrategy'
import Model from 'src/models/model'
import { TYPES as SALES_CHANNEL_TYPES } from 'src/models/model/concerns/SalesChannelRestrictable'
import { useAvailableItemVariations } from 'src/modules/checkout/composables/sales-availability/items/AvailableItemVariations'
import { useSalesChannel } from 'src/modules/checkout/composables/sales-channels/SalesChannel'
import { useDisableSales } from 'src/modules/register/composables/disable-sales'
import { useSalesRestrictions } from 'src/modules/register/composables/sales-restrictions/sales-restrictions'
import { availabilityBadgeColor, availableStock, hasReducedOpacity, isAvailabilityBadgeVisible, isOutOfStock } from 'src/services/inventory/sales-restrictions'

export default {
  name: 'ItemVariationButtons',
  configuration: ['currentSite'],
  context: ['currentUser'],
  props: {
    orderItem: {
      type: Object,
      required: true
    },
    itemOrderItem: {
      type: Object,
      required: true
    },
    itemVariations: {
      type: Array,
      required: true
    },
    displayPrice: {
      type: Boolean,
      required: false,
      default: true
    },
    round: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['click', 'request-authorization', 'edit-options-button-click'],
  setup (props) {
    const { salesChannel } = useSalesChannel(
      SALES_CHANNEL_TYPES.MENU,
      props.orderItem?.order
    )

    const { availableItemVariations } = useAvailableItemVariations(
      props.itemVariations,
      {
        salesChannel,
        orderItem: props.orderItem
      }
    )

    const { checkForSalesRestrictions } = useSalesRestrictions()

    const {
      editMode,
      toggleEditMode,
      areSalesEnabled,
      toggleSalesDisabled,
      hasEdits,
      saveEdits
    } = useDisableSales()

    return {
      availableItemVariations,
      editMode,
      toggleEditMode,
      areSalesEnabled,
      toggleSalesDisabled,
      hasEdits,
      saveEdits,
      // Sales Restrictions
      checkForSalesRestrictions
    }
  },
  data () {
    return {
      savingEdits: false
    }
  },
  computed: {
    inventoryStrategy () {
      return this.currentSite.activeInventoryStrategy
    },
    item () {
      return this.itemOrderItem.item
    },
    hide () {
      // We hide the item variation buttons if there is only one option available and it is already selected
      // This is to save space on the screen when mobile register
      const onlyOneOptionAvailable = this.availableItemVariations.length === 1
      const isOptionSelected = !_.isNil(this.itemOrderItem.itemVariation)

      if (onlyOneOptionAvailable && isOptionSelected) {
        return true
      }

      return false
    }
  },
  created () {
    _.extend(this, {
      configuration: Model.configuration,
      availabilityBadgeColor,
      availableStock,
      hasReducedOpacity,
      isAvailabilityBadgeVisible,
      isOutOfStock
    })
  },
  methods: {
    async handleSaveEdits () {
      try {
        const selectedItemVariation = this.itemOrderItem.itemVariation

        this.savingEdits = true
        await this.saveEdits()

        const shouldReplaceItemVariation = !this.areSalesEnabled(selectedItemVariation)
        if (shouldReplaceItemVariation) {
          const restrictOutOfStock = this.inventoryStrategy?.inventoryRestrictionType === SALES_ITEM_RESTRICTION_TYPES.RESTRICT
          // TODO OOS INDY-6757 Update this to use availableItemVariations - composable must be able to be told to ignore sales channel for stock checks?
          const replacementItemVariation = _(this.item.activeItemVariations).filter(activeItemVariation => {
            return this.areSalesEnabled(activeItemVariation) && (
              !restrictOutOfStock ||
              !this.isOutOfStock(activeItemVariation, { item: this.item, itemOrderItem: this.itemOrderItem, orderItem: this.orderItem })
            )
          }).first()

          if (!_.isNil(replacementItemVariation)) {
            this.toggleItemVariation(replacementItemVariation)
          }
        }
      } catch (e) {
        this.$rollbar.error(`Failed to save disable sales edits, exception: ${e.message}`)
      } finally {
        this.savingEdits = false
      }
    },
    toggleItemVariation (itemVariation) {
      this.itemOrderItem.toggleItemVariation(itemVariation)
      this.$emit('click', itemVariation)
    },
    handleClick (itemVariation) {
      if (this.editMode) return

      if (this.itemOrderItem.itemVariationSelected(itemVariation)) return

      this.checkForSalesRestrictions(
        itemVariation,
        { item: this.item, itemOrderItem: this.itemOrderItem, orderItem: this.orderItem },
        (adminApproved = false) => {
          this.itemOrderItem.adminApproved = adminApproved
          this.toggleItemVariation(itemVariation)
        }
      )
    }
  }
}

</script>
